import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { withRouter } from 'react-router';
import { withProps } from 'recompose';
import { TruncatedText } from '@/se/components/typography';
import LinkButton from '@/se/components/LinkButton';
import Modal from '@/se/components/Modal';
import Button from '@/se/components/Button[DEPRECATED]';
import graphqlSelectInput from '@/components/inputs/graphqlSelectInput';
import { useToaster } from '@/components/core/Toaster';
import { list, removeAndAssign } from '@/graph/procedureTypes';

import { ROOM_TYPE_LABELS } from '../room/enums';
import { sortNumber } from '../../../util/sort';
import ExpandableText from '../../core/ExpandableText';
import { Box, Typography } from '@material-ui/core';

export const nameColumn = {
  title: 'Name',
  key: 'name',
  lens: item => item.name,
  style: { maxWidth: '50em' },
  Component: props => <ExpandableText text={props.data} />,
};

export const expectedPreOpDurationColumn = {
  title: `Avg. time in ${ROOM_TYPE_LABELS.PRE_OP} (mins)`,
  key: 'expectedPreOpDuration',
  lens: item => item.expectedPreOpDuration,
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
  sortFunction: (l, r) => sortNumber(l.expectedPreOpDuration, r.expectedPreOpDuration),
};

export const expectedDurationColumn = {
  title: `Avg. time in ${ROOM_TYPE_LABELS.OR} (mins)`,
  key: 'expectedDuration',
  lens: item => item.expectedDuration,
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
  sortFunction: (l, r) => sortNumber(l.expectedDuration, r.expectedDuration),
};

export const expectedPacuDurationColumn = {
  title: `Avg. time in ${ROOM_TYPE_LABELS.PACU} (mins)`,
  key: 'expectedPacuDuration',
  lens: item => item.expectedPacuDuration,
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
  sortFunction: (l, r) => sortNumber(l.expectedPacuDuration, r.expectedPacuDuration),
};

const ProcedureTypeSelectInput = withProps({
  isClearable: false,
  useLegacy: true,
})(
  graphqlSelectInput({
    entityName: 'ProcedureType',
    placeholder: 'e.g., Neuroimaging',
    graphqlList: list,
    getOptionValue: option => (option ? parseInt(option.value, 10) : undefined),
    sortOptions: true,
  })
);

const removeAndAssignColumn = {
  title: 'Actions',
  lens: item => item,
  Component: withRouter(props => {
    const [mutate, { loading }] = useMutation(removeAndAssign, {
      variables: {
        id: props.data.id,
      },
      refetchQueries: [{ query: list }],
    });
    const [open, setOpen] = useState(false);
    const [procedureType, setProcedureType] = useState(null);
    const toaster = useToaster();

    const handleSubmit = async () => {
      try {
        await mutate({ variables: { id: props.data.id, assignTo: procedureType } });
        setOpen(false);
        props.history.goBack();
        toaster.success('Removed ' + props.data.name);
      } catch (e) {
        toaster.error('Unable to remove and assign procedure type: ' + e.message);
      }
    };

    return (
      <>
        <LinkButton type="button" danger onClick={() => setOpen(true)} busy={loading}>
          Remove and assign
        </LinkButton>
        {open && (
          <Modal title="Remove and assign" withRouter={false} onClose={() => setOpen(false)}>
            <Typography variant="h6" gutterBottom>
              {props.data.name}
            </Typography>
            <Typography variant="body2">Select procedure type to assign all procedures and references to.</Typography>
            <Box mt={2}>
              <ProcedureTypeSelectInput
                label="Assign to"
                value={procedureType}
                onChange={setProcedureType}
                transformOptions={procedureTypes =>
                  procedureTypes.filter(item => parseInt(item.id, 10) !== parseInt(props.data.id, 10))
                }
              />
            </Box>
            <Box mt={4} display="flex" gridGap="1rem">
              <Button
                disabled={loading || !procedureType || parseInt(procedureType, 10) === parseInt(props.data.id, 10)}
                danger
                onClick={handleSubmit}
              >
                Remove and assign
              </Button>
              <Button disabled={loading} onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </Box>
          </Modal>
        )}
      </>
    );
  }),
};

export const listColumns = [
  nameColumn,
  expectedPreOpDurationColumn,
  expectedDurationColumn,
  expectedPacuDurationColumn,
];

export const viewColumns = [
  expectedPreOpDurationColumn,
  expectedDurationColumn,
  expectedPacuDurationColumn,
  removeAndAssignColumn,
];
